<template>
    <div class="flex flex-col gap-6 w-full">
    <div class="flex flex-col border border-zinc-700 bg-zinc-900">
        <div
            class="grid grid-cols-[1fr_auto] p-4"
            data-testid="setup-selector-toggle"
            @click="expanded = !expanded"
        >
            <div class="flex-1">
                <div v-if="!selectedSetup?.name">{{ $t('setup_selector.no_setup') }}</div>
                <div class="flex gap-2" v-else>
                    <h2 class="mr-4 font-bold uppercase">
                        {{ selectedSetup?.name }}
                    </h2>
                    <span class="text-titles"> {{ $t('setup_card.winrate') }}: </span>
                    {{
                        stats(selectedSetup)?.winrate !== undefined
                            ? stats(selectedSetup)?.winrate + '%'
                            : $t('general.not_available')
                    }}
                </div>
            </div>
            <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                :class="
                    !expanded
                        ? 'rotate-180 transition-transform duration-300 ease-in-out'
                        : 'transition-transform duration-300 ease-in-out'
                "
            >
                <path
                    d="M17.293 15.707L12 10.4141L6.70703 15.707L5.29297 14.293L12 7.58594L18.707 14.293L17.293 15.707Z"
                    fill="white"
                />
            </svg>
        </div>
        <div
            class="grid overflow-hidden transition-all duration-300 ease-in-out"
            :class="expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'"
        >
            <div class="overflow-hidden">
                <div
                    class="grid grid-cols-[auto_1fr_auto] items-center gap-4 border-t-2 border-zinc-700 pr-4"
                    v-if="!seperateCreateSetupButton"
                    @click="navigateToCreateSetup"
                >
                    <div
                        class="bg-bg flex aspect-square items-center justify-center border-r-2 border-zinc-700 p-4"
                    >
                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-6 w-6"
                        >
                            <path
                                d="M21.6885 10.2744L16.8574 12.6895L15.209 19.2881L2.80469 21.1963L4.71289 8.79199L11.3105 7.14258L13.7256 2.3125L21.6885 10.2744ZM6.48633 10.4102L5.45215 17.1338L8.58594 14.001L8.09277 13.5078L9.50684 12.0938L11.9072 14.4932L10.4932 15.9082L10 15.415L6.86621 18.5479L13.5898 17.5137L14.8926 12.3076L11.6924 9.10742L6.48633 10.4102ZM13.2168 7.80371L16.1973 10.7842L18.3115 9.72656L14.2734 5.68848L13.2168 7.80371Z"
                                fill="#FF2C2C"
                            />
                        </svg>
                    </div>
                    <h2 class="font-bold uppercase">{{ $t('setup_selector.create_new') }}</h2>
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M8.29297 17.293L13.5859 12L8.29297 6.70703L9.70703 5.29297L16.4141 12L9.70703 18.707L8.29297 17.293Z"
                            fill="white"
                        />
                    </svg>
                </div>
                <div class="grid grid-cols-[1fr_auto_auto]">
                    <div
                        class="col-span-3 grid grid-cols-subgrid gap-3 p-4"
                        v-for="setup in dartSetups"
                        :key="setup.id"
                        data-testid="setup-option"
                        :data-setup-id="setup.id"
                        @click="selectSetup(setup)"
                        :class="
                            setup.id === (modelValue ?? matchStore.setup.dart_setup_id)
                                ? 'border-2 border-red-600'
                                : 'border-2 border-t-2 border-transparent border-t-zinc-700'
                        "
                    >
                        <h2 class="font-bold uppercase">
                            {{ setup.name }}
                        </h2>
                        <span class="text-titles"> {{ $t('setup_card.winrate') }}: </span>
                        {{
                            stats(setup)?.winrate !== undefined
                                ? stats(setup)?.winrate + '%'
                                : $t('general.not_available')
                        }}
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div
        v-if="seperateCreateSetupButton"
        class="grid grid-cols-[auto_1fr_auto] items-center gap-4 border-2 border-zinc-700 bg-zinc-900"
        @click="navigateToCreateSetup"
    >
        <div
            class="bg-bg flex aspect-square items-center justify-center border-r-2 border-zinc-700 p-4"
        >
            <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="h-6 w-6"
            >
                <path
                    d="M21.6885 10.2744L16.8574 12.6895L15.209 19.2881L2.80469 21.1963L4.71289 8.79199L11.3105 7.14258L13.7256 2.3125L21.6885 10.2744ZM6.48633 10.4102L5.45215 17.1338L8.58594 14.001L8.09277 13.5078L9.50684 12.0938L11.9072 14.4932L10.4932 15.9082L10 15.415L6.86621 18.5479L13.5898 17.5137L14.8926 12.3076L11.6924 9.10742L6.48633 10.4102ZM13.2168 7.80371L16.1973 10.7842L18.3115 9.72656L14.2734 5.68848L13.2168 7.80371Z"
                    fill="#FF2C2C"
                />
            </svg>
        </div>
        <div class="flex items-center justify-center gap-4">

            <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3">
                <path d="M7 -3.0598e-07L7 5L12 5L12 7L7 7L7 12L5 12L5 7L3.0598e-07 7L2.18557e-07 5L5 5L5 -2.18557e-07L7 -3.0598e-07Z" fill="#FF2C2C"/>
            </svg>
            <span class="text-red-600 uppercase font-bold leading-none mt-0.5">{{ $t('setup_selector.create_new') }}</span>
        </div>
    </div>
    </div>
</template>

<script>
import { useAuthStore } from '@/stores/auth.js'
import { useMatchStore } from '@/stores/match.js'
import { useMatchInviteStore } from '@/stores/matchInvite.js'
import { useDartSetupStore } from '@/stores/dartSetup.js'

export default {
    name: 'SetupsSelector',
    setup() {
        const authStore = useAuthStore()
        const matchStore = useMatchStore()
        const matchInviteStore = useMatchInviteStore()
        const dartSetupStore = useDartSetupStore()
        return { authStore, matchStore, matchInviteStore, dartSetupStore }
    },
    data() {
        return {
            expanded: false,
        }
    },
    props: {
        modelValue: {
            type: Number,
            required: false,
            default: null,
        },
        seperateCreateSetupButton: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    emits: ['update:modelValue'],
    computed: {
        dartSetups() {
            const setups = this.authStore.dart_setups || []
            return [...setups].sort((a, b) => {
                const gamesA = this.setupStats[a.id]?.[this.selectedGameType]?.games_played || 0
                const gamesB = this.setupStats[b.id]?.[this.selectedGameType]?.games_played || 0
                return gamesB - gamesA
            })
        },
        selectedSetup() {
            const selectedId = this.modelValue ?? this.matchStore.setup.dart_setup_id
            return this.dartSetups.find((setup) => setup.id === selectedId)
        },
        setupStats() {
            return this.authStore.statistics?.setups || {}
        },
        selectedGameType() {
            const matchtype = this.authStore.matchtypes?.find(
                (m) => m.id === this.matchStore.setup.matchtype_id,
            )
            if (!matchtype) return ''
            return 'matchtype_' + matchtype.name.toLowerCase().replace(/\s+/g, '_')
        },
    },
    methods: {
        stats(setup) {
            if (!this.selectedGameType) {
                const allStats = this.setupStats[setup.id]
                if (!allStats) return null
                const gameTypes = Object.keys(allStats)
                if (gameTypes.length === 0) return null
                return allStats[gameTypes[0]]
            }
            return this.setupStats[setup?.id]?.[this.selectedGameType]
        },
        selectSetup(selectedSetup) {
            this.$emit('update:modelValue', selectedSetup.id)
            this.expanded = false
        },
        navigateToCreateSetup() {
            // replace (not push): the whole create-setup detour should collapse
            // back into this single history slot when it's done, so that a
            // caller relying on router.back() (e.g. MatchSetupView) still lands
            // on whatever was there before, no matter how many hops happened
            // in between.
            if (this.matchInviteStore.isModalVisible) {
                this.matchInviteStore.hideInviteModal()
                this.dartSetupStore.setEntryPoint({ type: 'invite' })
                this.$router.replace({ name: 'setup-create' })
            } else {
                const matchId = this.$route.params.matchId
                this.dartSetupStore.setEntryPoint({
                    type: 'route',
                    name: this.$route.name,
                    params: { matchId },
                })
                this.$router.replace({ name: 'setup-create' })
            }
        },
    },
}
</script>
